import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
 
import StepModify from './otherComponent/StepModify';
import { ISign } from 'types/auth';
import CommonNodeSetting from './otherComponent/CommonNodeSetting/CommonNodeSetting';

type SignFormProps = {
  selectedNodeData: ISign;
  selectedNode: any;
  onSubmit: (data: ISign) => void;
  onClose: () => void;
};

type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

type NotificationPayload = {
  conditions: string[];
  users: string[];
  roles: string[];
  channels: string[];
};

const SignForm = ({ selectedNodeData, onSubmit, onClose, selectedNode }: SignFormProps) => {
  const [initialValues, setInitialValues] = useState<ISign | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({
    conditions: [],
    users: [],
    roles: [],
    channels: []
  });
  const [escalationPayload, setEscalationPayload] = useState({
    videoCall: { enabled: false, actors: [], users: [], callType: 'VIDEO' },
    chat: { enabled: false, actors: [], users: [] }
  });

  // Memoize the callback to prevent re-renders
  const handleStatusesLoaded = useCallback((list: StatusItem[]) => {
    console.log('📋 Statuses loaded:', list.length);
    setStatuses(list);
  }, []);

  // Memoize notification change handler to prevent re-renders
  const handleNotificationChange = useCallback((payload: NotificationPayload) => {
    console.log('🔔 Notification changed:', payload);
    setNotificationPayload(payload);
  }, []);

  // Memoize escalation change handler to prevent re-renders
  const handleEscalationChange = useCallback((payload: any) => {
    console.log('📞 Escalation changed:', payload);
    setEscalationPayload(payload);
  }, []);

  // Memoize step type change handler
  const handleStepTypeChange = useCallback((newType: string, setFieldValue: any, submitForm: any) => {
    console.log('🔄 Step type changing to:', newType);
    setFieldValue('stepType', newType);
    setFieldValue('componentType', newType);
    setTimeout(() => {
      submitForm();
    });
  }, []);

  useEffect(() => {
    if (selectedNodeData) {
      const { name, title, description, settings, component = {}, componentType, onComplete } = selectedNodeData;
      const actor = selectedNode?.data?.step?.actor?.map((a: any) => a?.uid) ?? [];

      // Extract and map notification data from backend
      const backendNotification = selectedNode?.data?.step?.notification || {};
      const mappedNotification = {
        scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
        onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
        notifyRoles: backendNotification.roles || [],
        notifyUsers: backendNotification.users || [],
        notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
          channel === 'IN-APP' ? 'In-App' : 
          channel === 'EMAIL' ? 'Email' :
          channel === 'SMS' ? 'SMS' : channel
        ) : []
      };

      setInitialValues({
        name,
        title,
        description,
        settings: {
          maxSize: settings?.maxSize ?? 0,
          width: settings?.width ?? undefined,
          height: settings?.height ?? undefined
        },
        component,
        componentType,
        data: {
          title: selectedNode?.data?.label
        },
        actor,
        stepType: selectedNode?.step?.stepType || 'ESIGN',
        onCompleteStatusUid: selectedNode?.data?.step?.onComplete?.status?.uid || '',
        onComplete: onComplete || {}
      });

      // Set notification payload separately to avoid type issues
      setNotificationPayload({
        conditions: [
          ...(mappedNotification.scheduleOnETA ? ['ON_ETA'] : []),
          ...(mappedNotification.onCompletion ? ['ON_COMPLETE'] : [])
        ],
        users: mappedNotification.notifyUsers || [],
        roles: mappedNotification.notifyRoles || [],
        channels: mappedNotification.notificationChannel && mappedNotification.notificationChannel.length > 0 
          ? mappedNotification.notificationChannel.map((channel: string) => 
              channel === 'In-App' ? 'IN-APP' : 
              channel === 'Email' ? 'EMAIL' :
              channel === 'SMS' ? 'SMS' : channel.toUpperCase()
            )
          : []
      });

      // Initialize escalation payload from existing node data
      setEscalationPayload(selectedNode?.data?.step?.escalation || {});
      // Pass the stored object straight through. Each form used to rebuild
      // this by hand from a fixed list of keys, which silently dropped any
      // field it did not know about — including `inherit`, so opening and
      // saving any step would reset it to the process default.
    }
  }, [selectedNodeData?.name, selectedNodeData?.title, selectedNodeData?.description, selectedNodeData?.settings, selectedNode?.data?.label, selectedNode?.data?.step?.actor, selectedNode?.data?.step?.notification, selectedNode?.data?.step?.escalation, selectedNode?.data?.step?.onComplete?.status?.uid]);

  // Memoize notification initial values to prevent Notification component re-renders
  const notificationInitialValues = useMemo(() => {
    // Extract notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    return {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };
  }, [selectedNode?.data?.step?.notification]);

  return (
    initialValues && (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          const selectedStatus = statuses.find((s) => s.uid === values.onCompleteStatusUid);
          
          const updatedData = {
            ...values,
            component: {
              ...values.component,
              name: values.name,
              componentType: values.componentType || 'ESIGN'
            },
            onComplete: selectedStatus ? { status: selectedStatus } : {}
          };

          // Add notification and escalation to the payload
          const finalData = {
            ...updatedData,
            notification: notificationPayload,
            escalation: escalationPayload
          };

          onSubmit(finalData as ISign);
          setSubmitting(false);
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, touched, errors, setFieldValue, submitForm }) => (
          <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h4" fontWeight="bold">
                {values.data.title}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {isEditingTitle ? (
                  <>
                    <TextField
                      name="data.title"
                      label="Description"
                      value={values.data.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus
                      size="small"
                    />
                    <Button variant="contained" size="small" sx={{ ml: 1 }} onClick={() => setIsEditingTitle(false)}>
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outlined" size="small" sx={{ ml: 1 }} onClick={() => setIsEditingTitle(true)}>
                      Edit
                    </Button>
                    {selectedNode && (
                      <StepModify
                        selectedNode={selectedNode}
                        onStepTypeChange={(newType) => handleStepTypeChange(newType, setFieldValue, submitForm)}
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>

            <Grid container spacing={2}>
               
              <Grid item xs={12}>
                <TextField fullWidth size="small" name="name" 
                label="Title"
                value={values.name} onChange={handleChange} onBlur={handleBlur} />
              </Grid>
 
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="description"
                  label="Description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" mt={4} mb={2}>
              Settings
            </Typography>
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography>Fields Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper sx={{ mt: 1, p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4} display="flex" alignItems="center">
                      <Typography>Max Size</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        name="settings.maxSize"
                        value={values.settings.maxSize}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.settings?.maxSize && Boolean(errors.settings?.maxSize)}
                        helperText={touched.settings?.maxSize && errors.settings?.maxSize}
                      />
                    </Grid>

                    <Grid item xs={4} display="flex" alignItems="center">
                      <Typography>Width</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        name="settings.width"
                        value={values.settings.width ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>

                    <Grid item xs={4} display="flex" alignItems="center">
                      <Typography>Height</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        name="settings.height"
                        value={values.settings.height ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </AccordionDetails>
            </Accordion>

              <CommonNodeSetting
              selectedNode={selectedNode}
              onStatusesLoaded={handleStatusesLoaded}
               onNotificationChange={handleNotificationChange}
                    notificationInitialValues={notificationInitialValues}
                escalationPayload={escalationPayload}
                onEscalationChange={handleEscalationChange}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button type="button" color="error" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Formik>
    )
  );
};

export default memo(SignForm);
